































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Types
import { IAffiliateTiersProviderMethods } from "@/models/providers/affiliateTiers";
import {
  IAffiliateTier,
  IAffiliateTierCondition,
  IAffiliateTierForm
} from "@/models/affiliate";

@Component({
  components: {
    "affiliate-tier-conditions-provider": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/affiliateTierConditionsProvider.vue"
      ),
    "affiliate-tier-conditions": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/affiliateTierConditions.vue"
      )
  }
})
export default class AddEditAffiliateTierModal extends Mixins(HasValidation) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    affiliateTierConditionsProvider: HTMLFormElement;
    name: HTMLFormElement;
  };

  // Props

  @Prop({ default: null })
  public affiliateTier!: IAffiliateTier;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public createAffiliateTier!: IAffiliateTiersProviderMethods["createAffiliateTier"];

  @Prop({ required: true })
  public updateAffiliateTier!: IAffiliateTiersProviderMethods["updateAffiliateTier"];

  // Data

  public isProcessing = false;

  public form: IAffiliateTierForm = {
    default: false,
    name: ""
  };
  public formClone: IAffiliateTierForm = _.cloneDeep(this.form);
  public formConditions: IAffiliateTierCondition[] = [];

  // Lifecycle methods

  public created() {
    this.fillForm();

    this.$nextTick(() => {
      this.$refs.name.focus();
    });
  }

  public fillForm() {
    if (this.isNewTier) return;

    this.form = _.pick(this.affiliateTier, ["default", "name"]);
    this.formClone = _.cloneDeep(this.form);
  }

  // Computed

  public get title() {
    return this.isNewTier
      ? this.$t("_.add_affiliate_tier")
      : this.$t("_.update_affiliate_tier");
  }

  public get isNewTier() {
    return _.isNil(this.affiliateTier);
  }

  public get affiliateTierId() {
    return _.get(this.affiliateTier, "id", null);
  }

  public get formIsDisabled() {
    return (
      this.isDisabled ||
      this.isProcessing ||
      this.hasErrors ||
      !this.formIsChanged
    );
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  // Methods

  public async validateBeforeSubmit() {
    if (this.isDisabled) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      if (!this.isNewTier) {
        await this.updateAffiliateTier(this.affiliateTier.id, this.form);
        this.$store.dispatch("toast/show", {
          message: this.$t("_.affiliate_tier_successfully_updated")
        });
      } else {
        const affiliateTier = await this.createAffiliateTier(this.form);

        await Promise.all(
          _.map(_.cloneDeep(this.formConditions), formCondition => {
            if (_.get(formCondition, "products", []).length) {
              formCondition["product_ids"] = _.map(
                formCondition.products,
                product => product.id
              );
            }
            return this.$refs.affiliateTierConditionsProvider.createTierCondition(
              _.omit(_.cloneDeep(formCondition), ["products", "id"]),
              affiliateTier.id
            );
          })
        );

        this.$store.dispatch("toast/show", {
          message: this.$t("_.affiliate_tier_successfully_created")
        });
      }

      this.$emit("close");
      this.$emit("success");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public openManageConditionModal(condition) {
    this.$refs.affiliateTierConditionsProvider.openManageConditionModal(
      _.get(condition, "id", null),
      condition,
      {
        success: formCondition => {
          if (!this.isNewTier) {
            this.$bus.$emit("refreshAffiliateTierConditions");
            return;
          }

          const index = _.findIndex(this.formConditions, [
            "id",
            formCondition.id
          ]);

          index !== -1
            ? this.$set(this.formConditions, `${index}`, formCondition)
            : this.formConditions.push({
                ...formCondition,
                order: this.formConditions.length
              });
        }
      }
    );
  }

  public deleteTierCondition(conditionId) {
    if (!this.isNewTier) {
      this.$refs.affiliateTierConditionsProvider.deleteTierCondition(
        conditionId
      );
      return;
    }

    this.formConditions = _.map(
      _.filter(
        this.formConditions,
        formCondition => formCondition.id !== conditionId
      ),
      (filteredCondition, index) => {
        filteredCondition.order = index;
        return filteredCondition;
      }
    );
  }

  public updateSorting(sortElems) {
    _.map(this.formConditions, condition => {
      condition.order = _.findIndex(
        sortElems,
        elemId => elemId === condition.id
      );
      return condition;
    });
  }
}
